import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { CheckCircle2, AlertTriangle, Lightbulb, Quote, Download } from 'lucide-react';
import type { GradeReport } from '../../types';

function gradeColor(n: number) {
  if (n >= 85) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  if (n >= 70) return 'text-brand-700 bg-brand-50 border-brand-100';
  if (n >= 55) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-red-600 bg-red-50 border-red-200';
}

export default function GradeReportView({ report, onDownload }: { report: GradeReport; onDownload?: () => void }) {
  const radarData = report.sections.map((s) => ({ section: s.name, score: s.score, fullMark: 100 }));

  return (
    <div className="space-y-8 animate-fade-in">
      {/* HEADER */}
      <div className="card p-6 md:p-8">
        <div className="flex flex-wrap items-start gap-6 justify-between">
          <div>
            <div className="text-xs text-ink-500">{report.fileName}</div>
            <h2 className="text-2xl md:text-3xl font-display font-bold mt-1">Evaluation Summary</h2>
            <p className="text-ink-600 mt-2 max-w-xl">{report.summary}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className={`w-24 h-24 md:w-28 md:h-28 rounded-full border-4 ${gradeColor(report.overallScore)} grid place-items-center`}>
                <div>
                  <div className="font-display font-bold text-3xl">{report.overallScore}</div>
                  <div className="text-xs -mt-0.5">/ 100</div>
                </div>
              </div>
              <div className="mt-2 text-sm font-semibold">{report.letterGrade}</div>
            </div>
            {onDownload && (
              <button onClick={onDownload} className="btn-secondary">
                <Download className="w-4 h-4" /> Export
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Criteria + Radar */}
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 card p-6">
          <h3 className="font-semibold">Criteria breakdown</h3>
          <div className="mt-4 space-y-3">
            {report.criteriaBreakdown.map((c) => (
              <div key={c.name}>
                <div className="flex justify-between text-sm">
                  <span className="text-ink-700">{c.name} <span className="text-ink-400 text-xs">· weight {c.weight}%</span></span>
                  <span className="font-semibold text-ink-900">{c.score}</span>
                </div>
                <div className="mt-1 h-2 bg-ink-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-brand-500 to-fuchsia-500" style={{ width: `${c.score}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 card p-6 min-h-[320px]">
          <h3 className="font-semibold">Section profile</h3>
          <div className="h-72 mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="section" tick={{ fontSize: 11, fill: '#475569' }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <Radar name="Score" dataKey="score" stroke="#4f46e5" fill="#6366f1" fillOpacity={0.35} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Per-section details */}
      <div>
        <h3 className="font-semibold text-lg mb-3">Section-by-section feedback</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {report.sections.map((s) => (
            <div key={s.name} className="card p-5">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">{s.name}</h4>
                <span className={`chip border ${gradeColor(s.score)}`}>{s.score}</span>
              </div>

              {s.strengths.length > 0 && (
                <FeedbackList icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />} title="Strengths" items={s.strengths} />
              )}
              {s.weaknesses.length > 0 && (
                <FeedbackList icon={<AlertTriangle className="w-4 h-4 text-amber-600" />} title="Weaknesses" items={s.weaknesses} />
              )}
              {s.recommendedActions.length > 0 && (
                <FeedbackList icon={<Lightbulb className="w-4 h-4 text-brand-600" />} title="How to improve" items={s.recommendedActions} />
              )}
              {s.evidenceQuotes && s.evidenceQuotes.length > 0 && (
                <div className="mt-3 p-3 bg-ink-50 border-l-4 border-brand-300 rounded-r">
                  <div className="flex items-center gap-2 text-xs text-ink-500 mb-1">
                    <Quote className="w-3.5 h-3.5" /> Evidence
                  </div>
                  <p className="text-sm text-ink-700 italic">"{s.evidenceQuotes[0]}"</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Improvement Plan */}
      <div className="card p-6 bg-gradient-to-br from-brand-50 to-white border-brand-100">
        <h3 className="font-semibold text-lg">Your improvement plan</h3>
        <p className="text-sm text-ink-600 mt-1">Do these in order — each item targets a specific weakness identified above.</p>
        <ol className="mt-4 space-y-2 list-decimal pl-5">
          {report.improvementPlan.map((step, i) => (
            <li key={i} className="text-ink-800">{step}</li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function FeedbackList({ icon, title, items }: { icon: React.ReactNode; title: string; items: string[] }) {
  return (
    <div className="mt-3">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-ink-500">
        {icon} {title}
      </div>
      <ul className="mt-1 space-y-1 text-sm text-ink-700">
        {items.map((x, i) => <li key={i} className="leading-relaxed">· {x}</li>)}
      </ul>
    </div>
  );
}
